<template>
  <div v-if="otjsReady" id="programme-otj-logs">
    <k-panel
      :hasContentToggle="true"
      title="OTJ Progress"
      :showContent="showOtjProgress"
      @toggle="showOtjProgress = $event"
      >
      <template #body>
        <otj-progress-chart v-if="useNewOtjProgressChart" :user-id="userId" :programme-id="programmeId" class="otj-progress-chart"></otj-progress-chart>
        <otj-progress-bar-chart v-else :user-id="userId" :programme-id="programmeId" class="otj-progress-chart"></otj-progress-bar-chart>
      </template>
    </k-panel>
    <k-panel :hasContentToggle="false">
      <template #body>
        <div class="panel-heading">
          <div class="header-left">
            <otj-progress :total-time-spent="totalTimeSpent" :planned-otj-hours="plannedOTJHours"></otj-progress>
          </div>
          <div class="header-right">
            <button class="btn btn-success" @click="openNewOtjModal">
              <i class="fas fa-edit" aria-hidden="true"></i> Add new O.T.J.
            </button>
          </div>
        </div>
        <k-nav-tabs :tabs="tabs" :current-tab="currentTab" :useRoutes="false" :useUrlQueryParams="true" @tab-selected="currentTab = $event"/>
        <k-panel :hasContentToggle="false">
          <template #body>
            <k-date-filters
            @filter-window-changed="onFilterWindowChanged"
            />
            <k-table
              :downloadable="true"
              :headers="kableHeaders"
              :rows="kableRows"
              :hideable="false"
              :panel="false"
              :max="10"
              :enableTopScroll="true"
              @clicked="handleTableAction"
              :empty-message="'There are no O.T.J. logs for this programme yet. Click the \'Add new O.T.J.\' button to get started.'"
            ></k-table>
          </template>
        </k-panel>
      </template>
    </k-panel>
    <k-modal :show="showCoachNotesModal" @close="showCoachNotesModal = false">
      <template #header>
        <h3>Coach's Notes</h3>
      </template>
      <template #body>
        <p class="coach-notes-text">{{ coachNotes || 'No feedback provided.' }}</p>
      </template>
      <template #footer>
        <button class="btn btn-primary" @click="showCoachNotesModal = false">Close</button>
      </template>
    </k-modal>
    <add-edit-otj
      :show-modal="showOtjModal"
      @close="showOtjModal = false"
      @update="getOtjLogs"
      :programme-id="programmeId"
      :learner-id="userId"
      :is-editing="isEditing"
      :otj-data="otjToEdit"
      :compliance-mode="false"
    ></add-edit-otj>
    <otj-submission-history
      :show="showSubmissionHistoryModal"
      :otj-entry-id="resubmittedOtj.id"
      @close="showSubmissionHistoryModal = false"
    ></otj-submission-history>
  </div>
</template>

<style scoped>
.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
}

.header-left,
.header-right {
  display: flex;
  gap: 10px;
}

.header-left {
  align-items: center;
}

.header-right {
  align-items: stretch;
}

.vertical-hr {
  height: 20px;
  width: 1px;
  background-color: var(--kate-border);
  margin: 0 10px;
}

p.coach-notes-text {
  background-color: var(--input-background);
  padding: 10px;
  border-radius: 8px;
  border: var(--input-border-alt);
}
</style>

<script>
import useGlobalStore from '@stores/global';
import KModal from '@base-components/k-modal.vue';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import TimeMixin from '@mixins/time-mixins';
import { sortObjectArray } from '@utils/sort-by-object-property';
import { LEARNING_ACTIVITY_TYPES } from '@constants';
import KDateFilters from '@base-components/buttons/k-date-filters.vue';
import KNavTabs from '@base-components/k-nav-tabs.vue';
import AddEditOtj from './otj_components/add-edit-otj.vue';
import OtjSubmissionHistory from './otj-submission-history.vue';
import OtjProgress from './otj_components/otj-progress.vue';
import OtjProgressChart from './otj_components/otj-progress-chart.vue';
import OtjProgressBarChart from './otj_components/otj-progress-bar-chart.vue';

export default {
  components: {
    OtjProgress,
    KModal,
    KTable,
    KPanel,
    AddEditOtj,
    OtjSubmissionHistory,
    OtjProgressChart,
    OtjProgressBarChart,
    KDateFilters,
    KNavTabs,
  },

  mixins: [TimeMixin],

  data() {
    return {
      otjs: [],
      otjsReady: false,
      showOtjModal: false,
      isEditing: false,
      otjToEdit: undefined,
      showCoachNotesModal: false,
      coachNotes: '',
      learningActivityTypes: LEARNING_ACTIVITY_TYPES,
      selectedStatuses: [],
      totalTimeSpent: 0,
      plannedOTJHours: 0,
      showSubmissionHistoryModal: false,
      resubmittedOtj: {},
      store: useGlobalStore(),
      showOtjProgress: true,
      filterWindowStart: null,
      filterWindowEnd: null,
      currentTab: 'all',
      tabs: [
        { name: 'All', key: 'all' },
        { name: 'Pending', key: 'pending' },
        { name: 'Approved', key: 'approved' },
        { name: 'Referred', key: 'referred' },
        { name: 'Draft', key: 'draft' },
      ],
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getOtjLogs();
  },

  watch: {
    programmeId(val) {
      if (val) {
        this.getOtjLogs();
      }
    },
  },

  computed: {
    useNewOtjProgressChart() {
      return this.$featureFlags.isEnabled('new-otj-progress-chart');
    },

    userId() {
      return this.store.userId;
    },

    programmeId() {
      return Number(this.$route.params.programmeId);
    },

    filteredOtjs() {
      let filteredOtjs;
      const otjsWithoutResubmitted = this.otjs.filter(otj => !otj.has_been_resubmitted);
      const normalizedTab = this.currentTab === 'referred' ? 'rejected' : this.currentTab;
      if (normalizedTab === 'all') {
        filteredOtjs = otjsWithoutResubmitted;
      } else {
        filteredOtjs = otjsWithoutResubmitted.filter(otj => otj.status.toLowerCase() === normalizedTab);
      }
      if (this.filterWindowStart && this.filterWindowEnd) {
        filteredOtjs = filteredOtjs.filter(otj => {
          const date = this.getDate(otj.start_time);
          return date >= this.filterWindowStart && date <= this.filterWindowEnd;
        });
      }
      return filteredOtjs;
    },

    kableHeaders() {
      return {
        date: {
          name: 'Date',
          type: 'datetime',
          sortable: true,
          filterable: true,
          filterBy: date => this.parseTimestamp(date, undefined, 'dd/MM/yyyy'),
          options: {
            datetimeFormat: 'dd/MM/yyyy',
          },
        },
        start_time: {
          name: 'Start',
          type: 'datetime',
          options: {
            datetimeFormat: 'h:mm a',
          },
        },
        end_time: {
          name: 'End',
          type: 'datetime',
          options: {
            datetimeFormat: 'h:mm a',
          },

        },
        time_spent: {
          name: 'Duration',
          type: 'string',
          sortable: true,
          filterable: true,
          filterBy: timeSpent => this.formatTimeSpent(timeSpent),
          displayAs: timeSpent => this.formatTimeSpent(timeSpent),
        },
        status: {
          name: 'Status',
          type: 'pill',
          hasMultiSelectDropdown: this.currentTab === 'all',
          options: {
            type: status => {
              const statusMap = {
                Approved: 'success',
                Referred: 'warning',
                Pending: 'primary',
                Draft: 'neutral',
              };
              return statusMap[status] || 'neutral';
            },
          },
        },
        learning_activity: {
          name: 'Activity',
          type: 'string',
        },
        description: {
          name: 'Description',
          type: 'string',
          filterable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
          filterable: true,
        },
        edit: {
          name: 'Edit',
          type: 'button',
          downloadAs: () => ' - ',
          options: {
            preset: 'edit',
            variant: row => (row.status?.toLowerCase() === 'referred' ? 'primary' : 'default'),
            title: row => {
              const status = row.status?.toLowerCase();
              if (status === 'referred') return 'Edit referred entry for re-submission';
              if (status === 'draft') return 'Edit draft entry';
              if (status === 'pending') return 'Edit pending entry';
              return 'Edit O.T.J. entry';
            },
            ariaLabel: row => {
              const status = row.status?.toLowerCase();
              if (status === 'referred') return 'Edit referred entry for re-submission';
              if (status === 'draft') return 'Edit draft entry';
              if (status === 'pending') return 'Edit pending entry';
              return 'Edit O.T.J. entry';
            },
            disabled: row => {
              const status = row.status?.toLowerCase();
              return status === 'approved' || (status === 'referred' && row.has_been_resubmitted);
            },
          },
        },
        note: {
          name: 'Coach Notes',
          type: 'button',
          downloadAs: cell => (cell?.otj_comment || ' - '),
          options: {
            preset: 'open',
            title: 'See reason for referral',
            ariaLabel: 'See reason for referral',
            disabled: row => !['referred', 'approved'].includes(row.status?.toLowerCase()),
          },
        },
        delete: {
          name: 'Delete',
          type: 'button',
          downloadAs: () => ' - ',
          options: {
            preset: 'delete',
            title: row => {
              const status = row.status?.toLowerCase();
              if (status === 'referred') return 'Delete referred entry';
              if (status === 'draft') return 'Delete draft entry';
              if (status === 'pending') return 'Delete pending entry';
              return 'Delete O.T.J. entry';
            },
            ariaLabel: row => {
              const status = row.status?.toLowerCase();
              if (status === 'referred') return 'Delete referred entry';
              if (status === 'draft') return 'Delete draft entry';
              if (status === 'pending') return 'Delete pending entry';
              return 'Delete O.T.J. entry';
            },
            disabled: row => row.status?.toLowerCase() !== 'draft',
          },
        },
        open_submission_history: {
          name: 'Submission History',
          type: 'button',
          downloadAs: () => ' - ',
          options: {
            icon: 'fas fa-history',
            title: 'View submission history',
            ariaLabel: 'View submission history',
          },
        },
      };
    },

    kableRows() {
      if (!this.otjsReady) {
        return [];
      }
      return sortObjectArray(this.filteredOtjs, 'created_at', true).map(otj => ({
        id: otj.id,
        date: otj.start_time,
        start_time: otj.start_time,
        end_time: otj.end_time,
        time_spent: otj.time_spent,
        status: this.statusDisplay(otj.status),
        learning_activity: otj.category,
        description: otj.learning_description,
        competencies: {
          tags: otj.competencies ? otj.competencies.map(c => c.label) : [],
          sortBy: otj.competencies ? otj.competencies.map(c => c.label).join(',') : '',
        },
        note: { otj_comment: otj.otj_comment || ' - ' },
        has_been_resubmitted: otj.has_been_resubmitted,
      }));
    },
  },

  methods: {
    getOtjLogs() {
      this.$Loading.start();
      this.otjsReady = false; // Set to false before fetching to show loading state
      this.$logger.info('Fetching OTJ logs', { programmeId: this.programmeId });
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/otj-entries`)
        .then(response => {
          this.$logger.info('Successfully fetched OTJ logs', { programmeId: this.programmeId });
          this.otjs = response.data.otj_entries || [];
          this.totalTimeSpent = response.data.total_time_spent;
          this.plannedOTJHours = response.data.planned_hours;
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching OTJ logs', { programmeId: this.programmeId }, err);
          this.$ktoast.warning('Error fetching OTJ logs');
          this.otjs = [];
        }).then(() => {
          this.otjsReady = true;
          this.$Loading.finish();
        });
    },

    deleteOtj(otjId) {
      this.$logger.info('Deleting OTJ', { id: otjId });
      this.$Loading.start();
      this.$http.delete(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${otjId}`)
        .then(() => {
          this.$logger.info('OTJ deleted', { id: otjId });
          this.$ktoast.success('OTJ deleted');
          this.getOtjLogs();
        })
        .catch(err => {
          this.$logger.autowarn('Error deleting OTJ', { id: otjId }, err);
          this.$ktoast.warning('Error deleting OTJ');
        }).then(() => {
          this.$Loading.finish();
        });
    },

    handleTableAction(key, index, row, column) {
      if (column === 'delete') {
        this.confirmDeleteOtj(row.id);
      } else if (column === 'edit') {
        this.otjToEdit = this.otjs.find(x => x.id === row.id);
        this.openEditOtjModal();
      } else if (column === 'note') {
        this.coachNotes = this.otjs.find(x => x.id === row.id).otj_comment;
        this.showCoachNotesModal = true;
      } else if (column === 'open_submission_history') {
        this.openSubmissionHistoryModal(row.id);
      }
    },

    confirmDeleteOtj(key) {
      this.$ktoast.confirmToast('Are you sure you want to delete this OTJ?', 'warning', () => {
        this.deleteOtj(key);
      });
    },

    statusDisplay(status) {
      const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      return status === 'rejected' ? 'Referred' : capitalizedStatus;
    },

    openNewOtjModal() {
      this.otjToEdit = undefined;
      this.showOtjModal = true;
      this.isEditing = false;
    },

    openEditOtjModal() {
      this.showOtjModal = true;
      this.isEditing = true;
    },

    findOtj(otjId) {
      return this.otjs?.find(otj => otj?.id === otjId);
    },

    openSubmissionHistoryModal(otjId) {
      const foundOtj = this.findOtj(otjId);
      if (!foundOtj) {
        return;
      }
      this.resubmittedOtj = foundOtj;
      this.showSubmissionHistoryModal = true;
    },
    onFilterWindowChanged({ start, end }) {
      this.filterWindowStart = start;
      this.filterWindowEnd = end;
    },
  },
};
</script>
